import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useDroppable } from '@dnd-kit/core';

import { useGetJobsAccountApplicantsQuery } from '../../services/serviceJobsAccount';
import {
  IJobApplicationStatus,
  IJobsAccountApplicant,
  IJobsAccountApplicantsRequest,
} from '../../services/serviceJobsAccount/interfaces';
import { ApplicantCard } from './ApplicantCard';

export const StageColumn = ({ status, baseRequest, busyApplicationId, onOpen }: {
  status: IJobApplicationStatus;
  baseRequest: IJobsAccountApplicantsRequest;
  busyApplicationId: number | null;
  onOpen: (applicant: IJobsAccountApplicant) => void;
}) => {
  const [loadedPages, setLoadedPages] = useState(1);
  const [collapsed, setCollapsed] = useState(
    status.name.toLowerCase() === 'rejected'
  );
  useEffect(() => setLoadedPages(1), [
    baseRequest.jobGuid,
    baseRequest.isRead,
    baseRequest.newThisWeekOnly,
    baseRequest.searchText,
  ]);
  const query = useGetJobsAccountApplicantsQuery({
    ...baseRequest,
    page: 1,
    pageSize: Math.min(loadedPages * 10, 100),
    statusId: status.id,
  }, {
    skip: collapsed,
  });
  const droppable = useDroppable({ id: `stage-${status.id}`, data: { statusId: status.id } });
  const applicants = query.data?.result || [];
  const total = query.data?.total ?? status.applicantCount;

  return (
    <Box
      ref={droppable.setNodeRef}
      sx={{
        backgroundColor: droppable.isOver ? 'var(--accent-soft)' : 'var(--bg-2)',
        border: droppable.isOver ? '2px dashed var(--accent)' : '1px solid var(--line)',
        flex: collapsed ? '0 0 170px' : '0 0 285px',
        minHeight: 260,
        p: 1.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography component="h3" fontWeight={800}>{status.name}</Typography>
        <Box sx={{ backgroundColor: 'var(--ink)', color: '#fff', fontFamily: 'var(--font-mono)', fontSize: '0.7rem', px: 1, py: 0.3 }}>
          {status.applicantCount}
        </Box>
      </Stack>
      {status.name.toLowerCase() === 'rejected' ? (
        <Button size="small" onClick={() => setCollapsed((value) => !value)} sx={{ mb: 1.5 }}>
          {collapsed ? 'Expand' : 'Collapse'}
        </Button>
      ) : null}
      {!collapsed && query.isLoading ? <CircularProgress size={24} /> : null}
      {!collapsed && query.error ? (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={() => query.refetch()}>
              Retry
            </Button>
          )}
          sx={{ mb: 1.5 }}
        >
          We could not load this stage.
        </Alert>
      ) : null}
      <Stack spacing={1.2}>
        {!collapsed && !query.error && applicants.map((applicant) => (
          <ApplicantCard
            key={applicant.applicationId}
            applicant={applicant}
            busy={busyApplicationId === applicant.applicationId}
            onOpen={onOpen}
          />
        ))}
      </Stack>
      {!collapsed && !query.isLoading && !query.error && applicants.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: '0.8rem', py: 3, textAlign: 'center' }}>
          No applicants in this stage.
        </Typography>
      ) : null}
      {!collapsed && !query.error && total > applicants.length && applicants.length < 100 ? (
        <Button
          size="small"
          disabled={query.isFetching}
          onClick={() => setLoadedPages((value) => value + 1)}
          sx={{ mt: 1.5 }}
        >
          {query.isFetching ? 'Loading…' : 'Load more'}
        </Button>
      ) : null}
      {!collapsed && !query.error && total > applicants.length && applicants.length >= 100 ? (
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mt: 1.5 }}>
          Use List view to review the remaining applicants.
        </Typography>
      ) : null}
    </Box>
  );
};

export default StageColumn;
